import type { paths } from '@cou/contracts';
import { adminApi, type ApiFailure } from '../api/admin-api';

/*
 * The Players API client (API-21, admin-ui.md → Players). The support entry point: Caesars support
 * supplies an account ref, ops searches by it. Injectable at the screen for tests, mirroring
 * apps/admin/src/contests/contests-client.ts.
 */

export type AdminPlayer =
  paths['/api/admin/users']['get']['responses'][200]['content']['application/json']['users'][number];
export type AdminPlayerDetail =
  paths['/api/admin/users/{id}']['get']['responses'][200]['content']['application/json'];

type ListPlayersBody =
  paths['/api/admin/users']['get']['responses'][200]['content']['application/json'];

export type ListPlayersResult = ({ ok: true } & ListPlayersBody) | ApiFailure;
export type GetPlayerResult = { ok: true; player: AdminPlayerDetail } | ApiFailure;

export type ListPlayersFilter = {
  /** The account ref to search for; a contains match server-side. */
  readonly search?: string;
  readonly segment?: string;
  readonly state?: string;
  readonly limit?: number;
  /** The opaque keyset cursor from a previous page. */
  readonly cursor?: string;
};

/** Browse game users, searchable by account ref (API-21 GET). */
export async function listPlayers(
  adminToken: string,
  filter: ListPlayersFilter,
): Promise<ListPlayersResult> {
  return adminApi.get<ListPlayersBody>(adminToken, '/api/admin/users', filter);
}

/** One player: the profile, their Entries, and their Awards (API-21 GET). */
export async function getPlayer(adminToken: string, id: string): Promise<GetPlayerResult> {
  const result = await adminApi.get<AdminPlayerDetail>(adminToken, `/api/admin/users/${id}`);
  if (!result.ok) return result;
  const { ok: _ok, ...player } = result;
  return { ok: true, player };
}

export interface PlayersClient {
  listPlayers(adminToken: string, filter: ListPlayersFilter): Promise<ListPlayersResult>;
  getPlayer(adminToken: string, id: string): Promise<GetPlayerResult>;
}

export const defaultPlayersClient: PlayersClient = { listPlayers, getPlayer };
